import {type FC, useEffect, useRef} from 'react';
import Hls from 'hls.js';
import {timeStringToSeconds} from '../../../utils/DateUtil.ts';
import {BackgroundVideo} from '../types/Streaming.styled.ts';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import {useLocation} from 'react-router';

interface IVodPlayer {
    videoUrl: string;
    startTime: string;
    endTime: string;
}

const VodPlayer: FC<IVodPlayer> = ({videoUrl, startTime, endTime}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const {isFullScreen} = useGlobalStore();
    const location = useLocation();
    const isHLS = videoUrl?.endsWith('.m3u8') ?? false;

    const getScheduleOffset = (duration: number): number => {
        if (duration <= 0 || !isFinite(duration)) return 0;

        const now = new Date(new Date().toLocaleString('en-US', {timeZone: 'Asia/Seoul'}));
        const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        const startPlaybackTime = timeStringToSeconds(startTime);

        const elapsedTime = currentSeconds - startPlaybackTime; // 시작부터 경과된 시간
        if (elapsedTime <= 0) return 0;

        return elapsedTime % duration; // 반복 재생 고려한 현재 위치
    };

    useEffect(() => {
        if (!videoUrl || !videoRef.current) return;
        const video = videoRef.current;

        let hasSeeked = false;
        const seekAndPlay = () => {
            if (hasSeeked) return;
            const duration = Math.floor(video.duration);
            if (duration <= 0 || !isFinite(duration)) return;
            hasSeeked = true;

            const offset = getScheduleOffset(duration);
            if (offset > 0) {
                video.currentTime = offset;
            }
            video.play().catch(() => {});
        };

        video.addEventListener('canplay', seekAndPlay);

        if (isHLS && Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(videoUrl);
            hls.attachMedia(video);

            return () => {
                video.removeEventListener('canplay', seekAndPlay);
                hls.destroy();
            };
        } else {
            video.src = videoUrl;

            return () => {
                video.removeEventListener('canplay', seekAndPlay);
            };
        }
    }, [videoUrl, isHLS, startTime, endTime]);

    return (
        <BackgroundVideo
            $isFullScreen={isFullScreen}
            $isWatching={location.pathname === '/watching'}
        >
            <video
                ref={videoRef}
                controls={false}
                loop={true}
                width="100%"
                muted={location.pathname === '/watching'}
            />
        </BackgroundVideo>
    );
};

export default VodPlayer;
